'use client';

import * as React from 'react';

import { useSession } from '@lib/auth/client';

import type { ListingBySlugPartial } from '@utils/imperative-node-queries';
import { useRouter } from 'next/navigation';
import { routes } from '@routes';
import { useFavoritedListingIds, useToggle } from '@hooks';

import { useToggleFavorite } from '@components/favorite-button';
import { Icon, IconNames } from '@components/icon';

import { ShareModal } from './share-modal';

interface ListingHeaderActionsProps {
  listing: ListingBySlugPartial;
}

export const ListingHeaderActions: React.FC<ListingHeaderActionsProps> = ({
  listing,
}) => {
  const { data: session, status } = useSession();
  const router = useRouter();
  const toggleFavorite = useToggleFavorite();

  const {
    on: shareModalActive,
    setOff: deactivateShareModal,
    setOn: activateShareModal,
  } = useToggle();

  const {
    isFavorited: isListingFavorited,
    setFavorited,
    isLoading: isFavoritesLoading,
  } = useFavoritedListingIds();
  const isFavorited = isListingFavorited(listing.id);
  const [isFavoriting, setIsFavoriting] = React.useState(false);
  const isFavoriteDisabled =
    isFavoriting || isFavoritesLoading || status === 'loading';

  async function handleFavorite(listingId: string): Promise<void> {
    if (session?.user == null) {
      router.push(routes.login, { scroll: false });
      return;
    }

    setIsFavoriting(true);
    setFavorited(listingId, !isFavorited);
    const isNowFavorited = await toggleFavorite(listingId, isFavorited);
    setFavorited(listingId, isNowFavorited ?? isFavorited);
    setIsFavoriting(false);
  }

  return (
    <>
      <ShareModal
        isOpen={shareModalActive}
        closeModal={deactivateShareModal}
        listing={listing}
      />
      <div className="hidden items-center sm:-mx-4 sm:flex sm:justify-between h-12">
        <div className="overflow-hidden"></div>
        <div className="hidden items-center justify-end text-primary-main sm:flex sm:w-28 pt-1">
          <button
            type="button"
            aria-label={`Favorite ${listing.name || listing.id}`}
            className="hidden w-full rounded-lg hover:bg-gray-xlight focus:outline-none sm:block py-2"
            disabled={isFavoriteDisabled}
            aria-busy={isFavoriteDisabled}
            onClick={() => handleFavorite(listing.id)}
          >
            <Icon
              name={IconNames.HEART}
              size={'lg'}
              solid={isFavorited ? true : false}
              className="text-primary-main"
            />
          </button>

          <button
            type="button"
            aria-label="Share listing"
            className="hidden w-full rounded-lg hover:bg-gray-xlight focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-main sm:block py-2"
            onClick={activateShareModal}
          >
            <Icon
              name={IconNames.SHARE}
              size={'lg'}
              className="text-primary-main"
            />
          </button>
        </div>
      </div>
    </>
  );
};
